import React, { FC } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import SubmitButton from 'components/SubmitButton';
import patterns from 'helpers/patterns';
import { useSendEmailPin } from 'modules/corporateOffer/hooks/useSendEmailPin';
import { useTempSignIn } from 'modules/corporateOffer/hooks/useTempSignIn';

import Input from '../inputs/Input';
import PasscodeInput from '../inputs/PasscodeInput';
import SuccessResult from '../SuccessResult';

import { FIELDS, TEMPLATE_INPUT_ID } from './constants';
import {
  LinkContainer,
  NoteText,
  PasscodeRecoveryLink,
  SignInForm,
} from './SignInStep';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';
import { EMAIL_INPUT_ID, emailValidationSchema } from './validationSchemas';
import { Redline } from './VerifyEmailStep';

const validationSchema = yup
  .object({
    [TEMPLATE_INPUT_ID]: yup
      .string()
      .length(FIELDS.length, 'Invalid PIN code')
      .matches(patterns.passcodePattern, {
        message: 'Only numbers allowed',
        excludeEmptyString: true,
      })
      .required('This field is required'),
  })
  .concat(emailValidationSchema);

type FormData = yup.InferType<typeof validationSchema>;

type Props = {
  onSuccess: (accessToken: string, refreshToken: string, email: string) => void;
};

const RecoveryStep: FC<Props> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    getValues,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      EMAIL_INPUT_ID: email ?? '',
      TEMPLATE_INPUT_ID: '',
    },
  });

  const {
    mutate: tempSignIn,
    isLoading: isLoadingTempSignIn,
    isError: isTempSignInError,
  } = useTempSignIn({
    onSuccess: (data) => {
      onSuccess(
        data.token,
        data.refreshToken,
        getValues(EMAIL_INPUT_ID) || email || ''
      );
    },
  });
  const {
    mutate: sendEmailPin,
    isLoading: isLoadingPin,
    isError: isPinError,
    error,
  } = useSendEmailPin({});
  const isError = isTempSignInError || isPinError;
  const isLoading = isLoadingTempSignIn || isLoadingPin;

  const onSubmit = async (data: FormData) => {
    tempSignIn({
      email: data.EMAIL_INPUT_ID,
      emailPin: data.TEMPLATE_INPUT_ID,
    });
  };

  const handleDidNotReceivePin = () => {
    const val = getValues(EMAIL_INPUT_ID) || email;

    if (!val) return;

    sendEmailPin({ email: val });
  };

  return (
    <Container>
      {isLoadingPin ? (
        <SuccessResult
          title="We have emailed you a temporary PIN code"
          subtitle="Please enter this to set a new passcode"
        />
      ) : (
        <>
          <header>
            <OnboardingTitle
              title={isError ? 'Invalid email and/or passcode' : 'Sign in'}
              error={isError}
            />
            <OnboardingSubtitle
              subtitle={`Please ${
                isError ? 'confirm' : 'enter'
              } your email & temporary PIN code`}
              error={isError}
            />
            <Redline />
          </header>
          <SignInForm onSubmit={handleSubmit(onSubmit)}>
            <Input<FormData>
              label="Email"
              inputId={EMAIL_INPUT_ID}
              inputType="email"
              control={control}
              register={register}
              error={
                error?.message
                  ? ({ message: error.message } as FieldError)
                  : errors.EMAIL_INPUT_ID
              }
            />
            <PasscodeInput<FormData>
              label="PIN Code"
              inputId={TEMPLATE_INPUT_ID}
              control={control}
              register={register}
              error={errors.TEMPLATE_INPUT_ID}
            />
            <ButtonWrapper data-testid="submit-button-wrapper">
              <SubmitButton
                text="SIGN IN"
                isDisabled={Object.keys(errors).length > 0 || isLoading}
                borderSize="xl"
                buttonSize="xl"
                fontSize="md"
              />
            </ButtonWrapper>
          </SignInForm>
          <LinkContainer>
            <PasscodeRecoveryLink onClick={handleDidNotReceivePin}>
              Didn’t receive your temporary PIN?
            </PasscodeRecoveryLink>
          </LinkContainer>
          <NoteText>
            This page is only for managing your account details.{'\n'}To use
            Shido, download the app to your phone and sign in there.
          </NoteText>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  margin: 38px auto 0;
  display: flex;
  justify-content: center;
`;

export default RecoveryStep;
